/* ==========================================================================
   SOUNDLABZ — motion.js
   The layer that makes it feel like an app instead of a website.

   Every module here is progressive: if the browser lacks the API, or the
   visitor has reduced motion on, nothing breaks — the effect just doesn't
   happen. None of it is load-bearing.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(pointer: fine)').matches;
  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  /* ------------------------------------------------- cross-page morphs */
  /* Handled declaratively in base.css via @view-transition. The browser
     does the morph itself on a normal navigation — no fetching, no
     intercepting, no way for it to break a link. Supporting browsers get
     the effect; everyone else gets an ordinary page load.

     All this needs from JS is naming the two elements that should pair up. */
  function tagPairs() {
    var hero = $('.pdp__media-main img');
    if (hero) hero.style.viewTransitionName = 'product-media';
    var h1 = $('.pdp__title');
    if (h1) h1.style.viewTransitionName = 'product-title';
  }

  /* --------------------------------------------------- the ring cursor */
  /* Your logo mark, following the pointer. Sticks to and swells around
     anything interactive, so the brand ends up doing the pointing.        */
  function cursor() {
    if (reduced || !fine) return;
    if ($('.ring-cursor')) return;

    var el = document.createElement('div');
    el.className = 'ring-cursor';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<svg viewBox="0 0 40 40"><defs><linearGradient id="rc" x1="0" y1="0" x2="1" y2="1">' +
      '<stop offset="0%" stop-color="#22c3ee"/><stop offset="55%" stop-color="#00e05a"/>' +
      '<stop offset="100%" stop-color="#00b84a"/></linearGradient></defs>' +
      '<circle cx="20" cy="20" r="17" fill="none" stroke="url(#rc)" stroke-width="1.6"/>' +
      '<circle cx="20" cy="20" r="14" fill="none" stroke="url(#rc)" stroke-width="1" opacity=".5"/></svg>';
    document.body.appendChild(el);

    var tx = innerWidth / 2, ty = innerHeight / 2, cx = tx, cy = ty;

    window.addEventListener('mousemove', function (e) {
      tx = e.clientX; ty = e.clientY;
      el.classList.add('is-live');
    }, { passive: true });

    document.addEventListener('mouseleave', function () { el.classList.remove('is-live'); });

    document.addEventListener('mouseover', function (e) {
      var t = e.target.closest('a, button, [role="button"], input, select, textarea, .card, label');
      el.classList.toggle('is-hot', !!t);
    });

    document.addEventListener('mousedown', function () { el.classList.add('is-down'); });
    document.addEventListener('mouseup', function () { el.classList.remove('is-down'); });

    (function loop() {
      // Lag is the whole effect — the ring chases, it doesn't stick.
      cx += (tx - cx) * 0.18;
      cy += (ty - cy) * 0.18;
      el.style.transform = 'translate3d(' + (cx - 20) + 'px,' + (cy - 20) + 'px,0)';
      requestAnimationFrame(loop);
    })();
  }

  /* ---------------------------------------------------- magnetic pulls */
  function magnets() {
    if (reduced || !fine) return;
    $$('.btn--volt, .btn--ink, [data-magnetic]').forEach(function (btn) {
      if (btn.__mag) return; btn.__mag = 1;
      var raf = 0;

      btn.addEventListener('mousemove', function (e) {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          var r = btn.getBoundingClientRect();
          var dx = e.clientX - (r.left + r.width / 2);
          var dy = e.clientY - (r.top + r.height / 2);
          btn.style.transform = 'translate(' + dx * 0.22 + 'px,' + dy * 0.3 + 'px)';
        });
      });

      btn.addEventListener('mouseleave', function () {
        cancelAnimationFrame(raf);
        btn.style.transform = '';
      });
    });
  }

  /* ------------------------------------------------------- 3D card tilt */
  function tilt() {
    if (reduced || !fine) return;
    $$('.card__media, [data-tilt]').forEach(function (el) {
      if (el.__tilt) return; el.__tilt = 1;
      var raf = 0;

      el.addEventListener('mousemove', function (e) {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          var r = el.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5;
          var py = (e.clientY - r.top) / r.height - 0.5;
          el.style.setProperty('--rx', (-py * 9).toFixed(2) + 'deg');
          el.style.setProperty('--ry', (px * 12).toFixed(2) + 'deg');
          // Drives the sheen sweep in CSS
          el.style.setProperty('--mx', ((px + 0.5) * 100).toFixed(1) + '%');
          el.style.setProperty('--my', ((py + 0.5) * 100).toFixed(1) + '%');
        });
      });

      el.addEventListener('mouseleave', function () {
        cancelAnimationFrame(raf);
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      });
    });
  }

  /* -------------------------------------------------- scramble decode */
  /* Headlines resolve character by character, like a signal locking on.   */
  var GLYPHS = '█▓▒░/\\<>*#0123456789ABCDEFGHJKLMNPQRSTUVWXYZ';

  function scramble() {
    if (reduced) {
      $$('[data-scramble]').forEach(function (el) { el.style.visibility = 'visible'; });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        run(en.target);
        io.unobserve(en.target);
      });
    }, { threshold: 0.4 });

    $$('[data-scramble]').forEach(function (el) {
      if (el.__scr) return; el.__scr = 1;
      el.dataset.text = el.textContent;
      io.observe(el);
    });
  }

  function run(el) {
    var text = el.dataset.text || el.textContent;
    var chars = text.split('');
    var done = new Array(chars.length).fill(false);
    // Each character gets its own settle time, so it resolves raggedly
    // rather than in a tidy left-to-right wipe.
    var settle = chars.map(function (c, i) {
      return c === ' ' ? 0 : 140 + i * 34 + Math.random() * 260;
    });
    var start = performance.now();
    el.style.visibility = 'visible';

    (function frame(now) {
      var t = now - start;
      var out = '', pending = false;
      for (var i = 0; i < chars.length; i++) {
        if (chars[i] === ' ') { out += ' '; continue; }
        if (t > settle[i]) { out += chars[i]; done[i] = true; }
        else {
          out += GLYPHS[(Math.random() * GLYPHS.length) | 0];
          pending = true;
        }
      }
      el.textContent = out;
      if (pending) requestAnimationFrame(frame);
      else el.textContent = text;
    })(start);
  }

  /* ---------------------------------------- native scroll-driven blur-in */
  /* Where supported this runs entirely off the main thread. theme.js's
     IntersectionObserver reveals stay as the fallback for everyone else.  */
  function nativeScroll() {
    if (reduced) return;
    if (!CSS.supports || !CSS.supports('animation-timeline: view()')) return;
    document.documentElement.setAttribute('data-native-scroll', '');
  }

  /* ------------------------------------------------------------ easter */
  /* Konami code drops the bass. Costs nothing, and the one customer who
     finds it will screenshot it.                                          */
  function konami() {
    var seq = [38,38,40,40,37,39,37,39,66,65], at = 0;
    window.addEventListener('keydown', function (e) {
      at = (e.keyCode === seq[at]) ? at + 1 : 0;
      if (at !== seq.length) return;
      at = 0;
      document.body.classList.add('is-dropped');
      if (window.SLZAudio) window.SLZAudio.sweep(60, 22, 2.2);
      if (window.SLZToast) window.SLZToast('BASS DROPPED 🔊');
      setTimeout(function () { document.body.classList.remove('is-dropped'); }, 2400);
    });
  }

  function boot() {
    magnets(); tilt(); scramble(); tagPairs();
  }

  function init() {
    nativeScroll(); cursor(); tagPairs(); konami(); boot();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', boot);
})();

/* Language switcher — posts Shopify's localization form on click. */
(function () {
  document.addEventListener('click', function (e) {
    var a = e.target.closest('.lang a[data-lang]');
    if (!a) return;
    e.preventDefault();
    var form = a.closest('form');
    if (!form) return;
    form.querySelector('input[name="language_code"]').value = a.getAttribute('data-lang');
    form.submit();
  });
})();
